import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { Message } from 'first-intl';
import { Token } from '@hugendubel/ws_client';
import { Query, UrlUtil } from '@hugendubel/utils';
import { LoginForm } from '@hugendubel/login';
import { Store } from '../../../store';
import { Globals } from '../../../globals';
import { InitService } from '../../../services/init-service';
import { isAuthenticated } from '../../../ws/WS';
import { ParticipationRequired } from './ParticipationRequired';
import { NotAvailablePage } from '../basic/NotAvailablePage';
import { AppFrame } from '../AppFrame';
import { externalHistory } from '../../hooks/useExposeNavigate';

type _LoginMaskType = {
    readAndRemoveUuid: () => string | undefined;
    content: (
        errorMsg: Message | undefined,
        isParticipating: boolean,
        initDone: boolean,
        children: ReactNode,
        finishParticipation: () => void
    ) => ReactNode;
};

export const _LoginMask: _LoginMaskType = {} as any;

_LoginMask.readAndRemoveUuid = (): string | undefined => {
    // read the uuid and then replace the history
    const query = UrlUtil.locationSearchToQuery(Globals.window().location.search) as Query; // get all search params
    const uuid = query.uuid; // read the uuid
    if (uuid) {
        delete query.uuid; // we want to remove the uuid param from search
        externalHistory.navigate(
            Globals.window().location.pathname.substring(process.env.BASENAME.length) + UrlUtil.queryToSearch(query),
            { replace: true }
        );
        return uuid;
    }
};

_LoginMask.content = (
    errorMsg: Message | undefined,
    isParticipating: boolean,
    initDone: boolean,
    children: ReactNode,
    finishParticipation: () => void
): ReactNode => {
    if (errorMsg) {
        return <NotAvailablePage error={errorMsg} />;
    } else if (!isParticipating) {
        return <ParticipationRequired finishParticipation={finishParticipation} />;
    } else if (!initDone) {
        return (
            <div className="container-fluid">
                <div className="row">
                    <div className="col col-xs-12 col-sm-6 customerLogin">
                        <LoginForm />
                    </div>
                </div>
            </div>
        );
    } else {
        return children;
    }
};

type LoginMaskState = {
    // the "firstCheck" will be set to "false" as soon as any information was acquired
    // to prevent rendering the login form when not necessary
    firstCheck: boolean;
    // the "initDone" is an indicator that the Config was initialized once with the customer related data
    initDone: boolean;
    isParticipating: boolean;
    error?: Message;
};

export type LoginMaskStoreProps = { wsToken: Token | null };
export type LoginMaskOwnProps = { children: ReactNode };
export type LoginMaskProps = LoginMaskStoreProps & LoginMaskOwnProps;

export const UnwiredLoginMask: React.FC<LoginMaskProps> = ({ wsToken, children }) => {
    const [{ firstCheck, initDone, isParticipating, error }, setState] = useState<LoginMaskState>({
        firstCheck: true,
        initDone: false,
        isParticipating: true,
        error: undefined,
    });

    const finishParticipation = useCallback(() => setState(s => ({ ...s, isParticipating: true, initDone: true })), []);

    useEffect(() => {
        const uuid = _LoginMask.readAndRemoveUuid();
        if (process.env.IS_HUDU && uuid) {
            InitService.initWithUuid(uuid).then(({ error, tokenWasCleared }) => {
                if (!tokenWasCleared) {
                    setState({ error, initDone: !error, firstCheck: false, isParticipating: true });
                }
            });
        } else if (isAuthenticated(wsToken || undefined)) {
            if (!initDone) {
                InitService.initWithToken().then(({ participates, error, tokenWasCleared }) => {
                    if (!tokenWasCleared) {
                        setState({
                            error,
                            initDone: !!participates,
                            firstCheck: false,
                            isParticipating: !!participates,
                        });
                    }
                });
            }
        } else {
            setState({ error: undefined, initDone: false, firstCheck: false, isParticipating: true });
        }
    }, [wsToken, initDone]);

    if (firstCheck) return null;

    return <AppFrame>{_LoginMask.content(error, isParticipating, initDone, children, finishParticipation)}</AppFrame>;
};

export const LoginMask = Store.wire<LoginMaskStoreProps, LoginMaskOwnProps>(UnwiredLoginMask, ({ wsToken }) => ({
    wsToken,
}));
